import { ComponentType } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
  CdwDropdownMenuModule,
  CdwDropdownMenuOption,
} from '@cdw-ae/common-ui-components-dropdown-menu';
import {
  FilterBarDialogComponent,
  FilterBarModule,
} from '@cdw-ae/filter-bar-core';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { FilterBarSelectionMatchersModule } from '@cdw-ae/filter-bar-selection-matchers';
import { FilterBarTemporalMatchersModule } from '@cdw-ae/filter-bar-temporal-matchers';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { FilterBarId } from '../../../../../common/services/route-config/models/route-config.model';
import {
  ChartActionTrigger,
  ChartType,
} from '../../../../../components/charts/models/chart.model';
import { SpogBarChartComponent } from '../../../../../components/charts/spog-bar-chart/spog-bar-chart.component';
import { SpogGaugeChartComponent } from '../../../../../components/charts/spog-gauge-chart/spog-gauge-chart.component';
import { SpogPieChartComponent } from '../../../../../components/charts/spog-pie-chart/spog-pie-chart.component';
import { REPORT_TYPES } from '../../reports/reports.model';
import {
  CardType,
  DashboardSummaries,
} from '../dashboard.model';
import {
  DashboardCardAction,
  DASHBOARD_FILTER_BAR_SETTINGS,
  DASHBOARD_FILTER_FIELD_MASK,
} from './dashboard-content.model';

@Component({
  selector: 'app-dashboard-content',
  templateUrl: './dashboard-content.component.html',
  styleUrls: ['./dashboard-content.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    NgxChartsModule,
    SpogPieChartComponent,
    SpogBarChartComponent,
    SpogGaugeChartComponent,
    CdwDropdownMenuModule,
    FilterBarModule,
    FilterBarSelectionMatchersModule,
    FilterBarTemporalMatchersModule,
    FilterBarTextMatchersModule,
  ],
})
export class DashboardContentComponent {
  public readonly ChartType = ChartType;
  public readonly CardType = CardType;

  @Input() dashboardData!: DashboardSummaries;
  @Input() isDevicesLastActiveSummaryDataLoading = false;
  @Input() isDevicesAueSummaryDataLoading = false;
  @Input() isDevicesVersionSummaryDataLoading = false;
  @Input() isDevicesBatteryHealthSummaryDataLoading = false;
  @Input() deviceSearchFilterBarId!: FilterBarId;
  @Input() selectedFilters$?: Observable<SelectedFilterField[]>;
  @Input() currentFilters: SelectedFilterField[] = [];
  @Input() actionTrigger?: Subject<ChartActionTrigger>;

  @Output() cardAction = new EventEmitter<DashboardCardAction>();
  @Output() aggregateReportSelect = new EventEmitter<CdwDropdownMenuOption>();
  @Output() filtersChange = new EventEmitter<SelectedFilterField[]>();

  public readonly tertiaryBlueCta: string = TailwindColors.tertiaryBlueCta;

  public readonly filterBarSettings: FilterBarField[] = DASHBOARD_FILTER_BAR_SETTINGS;
  public readonly fieldMask$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(
    DASHBOARD_FILTER_FIELD_MASK
  );
  public readonly filterBarDialogComponent: ComponentType<unknown> =
    FilterBarDialogComponent;

  public aggregateReportMenuOptions: CdwDropdownMenuOption[] = [
    {
      label: 'Model/auto-update expiration (AUE) by OU',
      value: REPORT_TYPES.MODEL_AUE,
    },
    {
      label: '15-day activity by OU',
      value: REPORT_TYPES.FIFTEEN_DAY_ACTIVITY,
    },
    {
      label: 'Chrome Version Age by OU',
      value: REPORT_TYPES.CHROME_VERSION_AGE,
    },
    {
      label: 'Battery health by OU',
      value: REPORT_TYPES.BATTERY_HEALTH,
    },
    {
      label: 'Storage by OU',
      value: REPORT_TYPES.STORAGE_BY_OU,
    },
  ];

  // Data checking methods
  public hasBarData(): boolean {
    return (
      !!this.dashboardData.deviceLastActiveSummary &&
      Object.keys(this.dashboardData.deviceLastActiveSummary).length > 0
    );
  }

  public hasPieData(): boolean {
    return (
      !!this.dashboardData.deviceAueSummary &&
      Object.keys(this.dashboardData.deviceAueSummary).length > 0
    );
  }

  public hasGaugeData(): boolean {
    return (
      !!this.dashboardData.batteryHealthSummary &&
      Object.keys(this.dashboardData.batteryHealthSummary).length > 0
    );
  }
}
